import { mean, std, round, max, min } from 'mathjs';
import { isManualReading } from 'helpers/externalDataSources';
import { isAggregatedPreMeal } from 'libs/StatsCalculations';
export function calculateData(readings) {
    const preMealReadings = [];
    const postMealReadings = [];
    const strictPreMealReadings = [];
    const strictPostMealReadings = [];
    let manualReadingsCount = 0;
    let readingsWithoutFlag = 0;
    readings.forEach((item)=>{
        if (isManualReading(item)) {
            manualReadingsCount += 1;
        }
        if (item.flags === 'None') {
            readingsWithoutFlag += 1;
        }
        if (isAggregatedPreMeal(item.flags)) {
            preMealReadings.push(item);
        } else {
            postMealReadings.push(item);
        }
        if (item.flags === 'PreMeal') {
            strictPreMealReadings.push(item);
        } else if (item.flags === 'PostMeal') {
            strictPostMealReadings.push(item);
        }
    });
    const allReadingsCount = readings.length;
    return {
        preMealReadings,
        postMealReadings,
        strictPreMealReadings,
        strictPostMealReadings,
        manualReadingsCount,
        allReadingsCount,
        readingsWithoutFlag
    };
}
export function createKPIData(data) {
    const { readings = [], preMealReadings = [], postMealReadings = [], strictPreMealReadings = [], strictPostMealReadings = [], daysWithReadings = 0, daysWithoutReadings = 0, standards, conversion } = data;
    const results = {
        daysWithReadings,
        daysWithoutReadings,
        lowReadings: 0,
        highReadings: 0,
        criticalLowReadings: 0,
        criticalHighReadings: 0
    };
    const readingsValues = readings.map((reading)=>reading.value);
    const preMealReadingsValues = preMealReadings.map((reading)=>reading.value);
    const postMealReadingsValues = postMealReadings.map((reading)=>reading.value);
    const strictPreMealReadingsValues = strictPreMealReadings.map((reading)=>reading.value);
    const strictPostMealReadingsValues = strictPostMealReadings.map((reading)=>reading.value);
    results.totalReadings = readingsValues.length;
    results.avgReadings = daysWithReadings + daysWithoutReadings === 0 ? 0 : +(results.totalReadings / (daysWithReadings + daysWithoutReadings)).toFixed(2);
    if (results.totalReadings > 0) {
        results.readingsAverage = mean(readingsValues);
        results.standardDeviation = std(readingsValues);
    } else {
        results.readingsAverage = null;
        results.standardDeviation = null;
    }
    if (preMealReadingsValues && preMealReadingsValues.length > 0) {
        preMealReadingsValues.forEach((readingValue)=>{
            if (standards) {
                if (standards.preMeal.lowThreshold > readingValue) {
                    results.lowReadings += 1;
                } else if (standards.preMeal.highThreshold < readingValue) {
                    results.highReadings += 1;
                }
                if (standards.critical.lowThreshold > readingValue) {
                    results.criticalLowReadings += 1;
                } else if (standards.critical.highThreshold < readingValue) {
                    results.criticalHighReadings += 1;
                }
            }
        });
        results.preMealAverage = mean(preMealReadingsValues);
    } else {
        results.preMealAverage = null;
    }
    if (postMealReadingsValues && postMealReadingsValues.length > 0) {
        postMealReadingsValues.forEach((readingValue)=>{
            if (standards) {
                if (standards.postMeal.lowThreshold > readingValue) {
                    results.lowReadings += 1;
                } else if (standards.postMeal.highThreshold < readingValue) {
                    results.highReadings += 1;
                }
                if (standards.critical.lowThreshold > readingValue) {
                    results.criticalLowReadings += 1;
                } else if (standards.critical.highThreshold < readingValue) {
                    results.criticalHighReadings += 1;
                }
            }
        });
        results.postMealAverage = mean(postMealReadingsValues);
    } else {
        results.postMealAverage = null;
    }
    if (strictPreMealReadingsValues.length) {
        results.strictPreMealAverage = conversion.toDisplay(mean(strictPreMealReadingsValues));
    } else {
        results.strictPreMealAverage = null;
    }
    if (strictPostMealReadingsValues.length) {
        results.strictPostMealAverage = conversion.toDisplay(mean(strictPostMealReadingsValues));
    } else {
        results.strictPostMealAverage = null;
    }
    // https://care.diabetesjournals.org/content/41/11/2275.figures-only
    results.GMI = results.readingsAverage && round(3.31 + 0.02392 * results.readingsAverage, 2);
    results.CV = results.readingsAverage && round(results.standardDeviation / results.readingsAverage * 100 || 0, 1);
    results.readingsAverage = results.readingsAverage && conversion.toDisplay(results.readingsAverage);
    results.postMealAverage = results.postMealAverage && conversion.toDisplay(results.postMealAverage);
    results.postMealCount = postMealReadingsValues.length;
    results.preMealCount = preMealReadingsValues.length;
    results.preMealAverage = results.preMealAverage && conversion.toDisplay(results.preMealAverage);
    results.strictPreMealCount = strictPreMealReadings.length;
    results.strictPostMealCount = strictPostMealReadings.length;
    results.standardDeviation = results.standardDeviation && conversion.toDisplay(results.standardDeviation);
    results.maxValue = readingsValues.length ? conversion.toDisplay(max(readingsValues)) : null;
    results.minValue = readingsValues.length ? conversion.toDisplay(min(readingsValues)) : null;
    return results;
}
export function calculateGlucoseConcentrationLevels(standards, conversion) {
    const { highThreshold: criticalHigh, lowThreshold: criticalLow } = standards.critical;
    const { highThreshold: targetHigh, lowThreshold: targetLow } = standards.postMeal;
    if (conversion.unit === 'MG_DL') {
        return [
            criticalHigh,
            targetHigh,
            targetLow,
            criticalLow,
            0
        ];
    }
    return [
        conversion.toDisplay(criticalHigh),
        conversion.toDisplay(targetHigh),
        conversion.toDisplay(targetLow),
        conversion.toDisplay(criticalLow),
        0
    ];
}
export function calculateGlucoseLevels(readings, conversion, levels) {
    const glucoseLevels = {
        veryHigh: {
            value: 0,
            percentage: 20
        },
        high: {
            value: 0,
            percentage: 20
        },
        target: {
            value: 0,
            percentage: 20
        },
        low: {
            value: 0,
            percentage: 20
        },
        veryLow: {
            value: 0,
            percentage: 20
        }
    };
    if (!readings || readings.length === 0) {
        return glucoseLevels;
    }
    readings.forEach((reading)=>{
        const lvlIndex = levels.findIndex((lvl, idx)=>idx >= 2 ? conversion.toDisplay(reading.value) >= lvl : conversion.toDisplay(reading.value) > lvl);
        const glucoseConcentrationLevel = Object.keys(glucoseLevels)[lvlIndex];
        glucoseLevels[glucoseConcentrationLevel].value += 1;
    });
    Object.keys(glucoseLevels).forEach((level)=>{
        const count = glucoseLevels[level].value;
        glucoseLevels[level].percentage = round(count / readings.length * 100, 0);
    });
    return glucoseLevels;
}
